/**
 * ProductCard Component
 * Displays a product card with image, title, and price
 */
import { Link } from "@tanstack/react-router";
import { Image, Money } from "@shopify/hydrogen-react";
import { useInView } from "framer-motion";
import {
  CardArrow,
  cardMedia,
  cardSurface,
} from "@modules/shared/Card/cardStyles";
import { useRef } from "react";
import i18n from "@modules/shared/localization/i18n";
import {
  BasePriceLine,
  cheapestVariantBasePrice,
} from "@modules/shared/Product/BasePrice";
import type { CurrencyCode } from "@shopify/hydrogen-react/storefront-api-types";

interface ProductCardProduct {
  id: string;
  title: string;
  handle: string;
  productType?: string;
  availableForSale: boolean;
  featuredImage?: {
    url?: string;
    altText?: string | null;
  } | null;
  priceRange: {
    minVariantPrice: {
      amount: string;
      currencyCode: CurrencyCode;
    };
  };
  compareAtPriceRange?: {
    minVariantPrice?: {
      amount?: string;
      currencyCode?: CurrencyCode;
    } | null;
  } | null;
  basePriceVariants?: {
    nodes: Array<{
      price: { amount?: unknown };
      basePrice?: { value?: string | null } | null;
    }>;
  } | null;
}

interface ProductCardProps {
  product: ProductCardProduct;
  locale: string;
}

export function ProductCard({ product, locale }: Readonly<ProductCardProps>) {
  const cardRef = useRef<HTMLAnchorElement>(null);
  const isInView = useInView(cardRef, {
    once: true,
    margin: "200px 0px",
  });
  const {
    title,
    handle,
    productType,
    featuredImage,
    priceRange,
    compareAtPriceRange,
    availableForSale,
    basePriceVariants,
  } = product;
  const basePrice = cheapestVariantBasePrice(basePriceVariants?.nodes ?? []);

  const comparePrice = compareAtPriceRange?.minVariantPrice;
  const hasDiscount =
    comparePrice?.amount &&
    comparePrice.currencyCode &&
    Number.parseFloat(comparePrice.amount) >
      Number.parseFloat(priceRange.minVariantPrice.amount);

  return (
    <Link
      ref={cardRef}
      to="/$locale/product/$handle"
      params={{ locale, handle }}
      data-product-handle={handle}
      className={`${cardSurface} ${isInView ? "opacity-100" : "opacity-0"}`}
      viewTransition
    >
      {/* Product photos come in different crops, many with white padding
          baked in. Containing them in a white frame lines them all up. */}
      <div
        className={`${cardMedia} aspect-square`}
        style={{ viewTransitionName: `product-image-${handle}` }}
      >
        {featuredImage?.url ? (
          <Image
            src={featuredImage.url}
            alt={featuredImage.altText || title}
            className="h-full w-full object-contain p-4 transition-transform duration-500 ease-out group-hover:scale-[1.04] motion-reduce:transition-none max-mobile:p-1.5"
            loading="lazy"
            sizes="(min-width: 1024px) 25vw, (min-width: 768px) 33vw, 50vw"
          />
        ) : (
          <div className="bg-background-6 h-full w-full" />
        )}
        {(!availableForSale || hasDiscount) && (
          <div className="absolute top-2 left-2 flex gap-1.5">
            {!availableForSale && (
              <span className="rounded-full bg-text-1 px-2.5 py-1 text-tny font-semibold text-white">
                {i18n.t("Common.soldOut")}
              </span>
            )}
            {hasDiscount && (
              <span className="rounded-full bg-primary px-2.5 py-1 text-tny font-semibold text-white">
                {i18n.t("Common.sale")}
              </span>
            )}
          </div>
        )}
      </div>
      <div className="flex flex-1 flex-col px-2 pt-3 pb-1.5">
        {productType && (
          <p className="truncate text-tny text-text-4">{productType}</p>
        )}
        <h3
          className="mt-0.5 line-clamp-2 text-base font-bold text-text-1"
          style={{ viewTransitionName: `product-title-${handle}` }}
        >
          {title}
        </h3>
        <div className="mt-auto flex items-center justify-between gap-2 pt-3">
          <div
            className="flex items-baseline gap-2 tabular-nums"
            style={{ viewTransitionName: `product-price-${handle}` }}
          >
            <Money
              data={priceRange.minVariantPrice}
              className={`text-small font-semibold ${hasDiscount ? "text-primary" : "text-text-1"}`}
            />
            {hasDiscount ? (
              <Money
                data={
                  comparePrice as { amount: string; currencyCode: CurrencyCode }
                }
                className="text-tny text-text-4 line-through"
              />
            ) : null}
          </div>
          <CardArrow className="max-mobile:hidden" />
        </div>
        <BasePriceLine basePrice={basePrice} className="mt-0.5" />
      </div>
    </Link>
  );
}

interface ProductGridProps {
  products: Array<ProductCardProps["product"]>;
  locale: string;
  columns?: 2 | 3 | 4 | 5;
}

export function ProductGrid({
  products,
  locale,
  columns = 4,
}: Readonly<ProductGridProps>) {
  const gridCols = {
    2: "grid-cols-2",
    3: "grid-cols-2 md:grid-cols-3",
    4: "grid-cols-2 md:grid-cols-3 lg:grid-cols-4",
    5: "grid-cols-2 md:grid-cols-3 lg:grid-cols-5",
  };

  return (
    <div className={`grid gap-4 max-mobile:gap-3 ${gridCols[columns]}`}>
      {products.map((product) => (
        <ProductCard key={product.id} product={product} locale={locale} />
      ))}
    </div>
  );
}
